import { WebAuthn } from '@gledly/capacitor-webauthn'

// Shared WebAuthn/base64url helpers and small fetch wrappers

export function b64uToArrayBuffer(b64u) {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4)
  const b64 = (b64u + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) { arr[i] = raw.charCodeAt(i) }
  return arr.buffer
}

export function arrayBufferToB64u(buf) {
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) { binary += String.fromCharCode(bytes[i]) }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function postJson(url, body, csrf) {
  return await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf }, body: JSON.stringify(body), credentials: 'same-origin' })
}

export async function patchJson(url, body, csrf) {
  return await fetch(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf }, body: JSON.stringify(body), credentials: 'same-origin' })
}

export async function deleteReq(url, csrf) {
  return await fetch(url, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf }, credentials: 'same-origin' })
}

export function isCapacitorNative() {
  return !!window.Capacitor?.isNativePlatform?.()
}

export function toB64u(maybeBufOrString) {
  // Plugin will likely return base64url strings already.
  if (typeof maybeBufOrString === 'string') return maybeBufOrString
  return arrayBufferToB64u(maybeBufOrString)
}

export async function passkeysSupported() {
  // Support if WebAuthn in browser OR native bridge available in Capacitor
  const webSupported = !!window.PublicKeyCredential
  console.log('webSupported', webSupported, 'isNative', window.Capacitor?.isNativePlatform?.())

  if (window.Capacitor?.isNativePlatform?.()) {
    try {
      const res = await WebAuthn.isAvailable()
      console.log('WebAuthn.isAvailable()', res)
      return webSupported || res.available
    } catch (e) {
      console.warn('WebAuthn.isAvailable threw', e)
    }
  }

  return webSupported
}

export async function getAssertion(serverOptions) {
  if (isCapacitorNative() && !window.PublicKeyCredential) {
    const result = await WebAuthn.startAuthentication({
      challenge: serverOptions.challenge,
      allowCredentials: serverOptions.allowCredentials,
      userVerification: serverOptions.userVerification,
      rpId: serverOptions.rpId ?? serverOptions.rp?.id,
      timeout: serverOptions.timeout
    })

    return {
      id: result?.id,
      rawId: toB64u(result?.rawId),
      type: result?.type ?? 'public-key',
      response: {
        clientDataJSON: toB64u(result?.response?.clientDataJSON),
        authenticatorData: toB64u(result?.response?.authenticatorData),
        signature: toB64u(result?.response?.signature),
        userHandle: result?.response?.userHandle ? toB64u(result?.response?.userHandle) : null
      }
    }
  }

  const options = structuredClone(serverOptions)
  if (options.challenge) {
    options.challenge = b64uToArrayBuffer(options.challenge)
  }
  if (Array.isArray(options.allowCredentials)) {
    options.allowCredentials = options.allowCredentials.map((cred) => ({
      ...cred,
      id: b64uToArrayBuffer(cred.id)
    }))
  }

  const credential = await navigator.credentials.get({ publicKey: options })

  return {
    id: credential.id,
    rawId: toB64u(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toB64u(credential.response.clientDataJSON),
      authenticatorData: toB64u(credential.response.authenticatorData),
      signature: toB64u(credential.response.signature),
      userHandle: credential.response.userHandle ? toB64u(credential.response.userHandle) : null
    }
  }
}

export async function createCredential(serverOptions) {
  if (isCapacitorNative() && !window.PublicKeyCredential) {
    const rpId = serverOptions.rpId ?? serverOptions.rp?.id

    const cred = await WebAuthn.startRegistration({
      challenge: serverOptions.challenge,
      rp: {
        id: rpId,
        name: serverOptions.rp?.name
      },
      user: {
        id: serverOptions.user.id,
        name: serverOptions.user.name,
        displayName: serverOptions.user.displayName
      },
      pubKeyCredParams: serverOptions.pubKeyCredParams,
      timeout: serverOptions.timeout,
      attestation: serverOptions.attestation,
      authenticatorSelection: serverOptions.authenticatorSelection,
      excludeCredentials: serverOptions.excludeCredentials
    })

    return {
      id: cred.id,
      rawId: toB64u(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: toB64u(cred.response.clientDataJSON),
        attestationObject: toB64u(cred.response.attestationObject)
      }
    }
  }

  const options = structuredClone(serverOptions)
  options.challenge = b64uToArrayBuffer(options.challenge)
  options.user.id = b64uToArrayBuffer(options.user.id)
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map((c) => ({
      ...c,
      id: b64uToArrayBuffer(c.id)
    }))
  }
  if (options.authenticatorSelection) {
    options.authenticatorSelection.residentKey = options.authenticatorSelection.residentKey ?? 'preferred'
  }

  const cred = await navigator.credentials.create({ publicKey: options })

  return {
    id: cred.id,
    rawId: toB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64u(cred.response.clientDataJSON),
      attestationObject: toB64u(cred.response.attestationObject)
    }
  }
}

// Optional global exposure for inline uses when needed
if (typeof window !== 'undefined') {
  window.WebAuthnUtils = { b64uToArrayBuffer, arrayBufferToB64u, postJson, patchJson, deleteReq }
}
